import React from 'react'

import {
    Checkbox,
    Chip,
    Divider,
    FormControl,
    InputAdornment,
    InputLabel,
    ListSubheader,
    MenuItem,
    Select,
    Slider,
    TextField,
    useTheme,
} from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab'

import { Container, Finder, SideDrawer } from '../../../components'
import { typographyStyle } from '../../../theme/typographyStyle'
import { useMultiSelectStyles } from '../../../styles'
import {
    ActionItemFilterState,
    FlagFilter,
    ConfidenceOperator,
    SuggestionDecision,
} from '../../../hooks/useActionItemSuggestions/types'
import { ApartmentVendor, Folder, Service } from '../../../models'
import { Location, LocationSelection } from '../../../store'

interface ActionItemFilterDrawerProps {
    open: boolean
    onClose: () => void
    tree: Folder
    locationSelection: LocationSelection
    onSelectLocation: (location: Location) => void
    filter: ActionItemFilterState
    setFilter: React.Dispatch<React.SetStateAction<ActionItemFilterState>>
    serviceList: Service[]
    apartmentVendorList: ApartmentVendor[]
}

export const ActionItemFilterDrawer: React.FC<ActionItemFilterDrawerProps> = ({
    open,
    onClose,
    tree,
    locationSelection,
    onSelectLocation,
    filter,
    setFilter,
    serviceList,
    apartmentVendorList,
}) => {
    const theme = useTheme()
    const classes = useMultiSelectStyles()
    const [localThreshold, setLocalThreshold] = React.useState(
        filter.confidenceThreshold,
    )
    const [serviceSearch, setServiceSearch] = React.useState('')
    const [vendorSearch, setVendorSearch] = React.useState('')
    const [serviceOpen, setServiceOpen] = React.useState(false)
    const [vendorOpen, setVendorOpen] = React.useState(false)

    React.useEffect(() => {
        setLocalThreshold(filter.confidenceThreshold)
    }, [filter.confidenceThreshold])

    const allServices = React.useMemo(
        () => serviceList.map((s) => ({ id: s.id, name: s.name })),
        [serviceList],
    )

    const allVendors = React.useMemo(() => {
        const selectedServiceIds = new Set(filter.services)
        return apartmentVendorList
            .filter(
                (av) =>
                    selectedServiceIds.size === 0 ||
                    av.vendor.services.some((s) =>
                        selectedServiceIds.has(s.id),
                    ),
            )
            .map((av) => ({ id: av.vendor.id, name: av.vendor.name }))
    }, [apartmentVendorList, filter.services])

    const filteredServices = React.useMemo(() => {
        if (!serviceSearch.trim()) return allServices
        const q = serviceSearch.toLowerCase()
        return allServices.filter((s) => s.name.toLowerCase().includes(q))
    }, [allServices, serviceSearch])

    const filteredVendors = React.useMemo(() => {
        if (!vendorSearch.trim()) return allVendors
        const q = vendorSearch.toLowerCase()
        return allVendors.filter((v) => v.name.toLowerCase().includes(q))
    }, [allVendors, vendorSearch])

    const labelStyle: React.CSSProperties = {
        ...typographyStyle(theme.typography.body2),
        fontWeight: 600,
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(0.5),
    }

    return (
        <SideDrawer
            open={open}
            handleClose={onClose}
            title="Filters"
            width={380}
        >
            <Container
                style={{
                    flex: 1,
                    flexDirection: 'column',
                    height: '100%',
                    overflow: 'hidden',
                }}
            >
                <Container
                    style={{
                        flexDirection: 'column',
                        padding: theme.spacing(1, 2),
                        overflowY: 'auto',
                        flex: 1,
                        gap: theme.spacing(2),
                    }}
                >
                    {/* Status */}
                    <Container style={{ flexDirection: 'column' }}>
                        <span style={labelStyle}>Status</span>
                        <ToggleButtonGroup
                            value={filter.statuses}
                            onChange={(_, newStatuses) =>
                                setFilter({
                                    ...filter,
                                    statuses: newStatuses as SuggestionDecision[],
                                })
                            }
                            aria-label="Status filter"
                            style={{ width: '100%' }}
                        >
                            <ToggleButton
                                value="pending"
                                aria-label="Pending"
                                style={{ flex: 1, textTransform: 'none' }}
                            >
                                Pending
                            </ToggleButton>
                            <ToggleButton
                                value="accepted"
                                aria-label="Accepted"
                                style={{ flex: 1, textTransform: 'none' }}
                            >
                                Accepted
                            </ToggleButton>
                            <ToggleButton
                                value="rejected"
                                aria-label="Rejected"
                                style={{ flex: 1, textTransform: 'none' }}
                            >
                                Rejected
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Container>

                    <Divider />

                    {/* Flag */}
                    <Container style={{ flexDirection: 'column' }}>
                        <span style={labelStyle}>Flag</span>
                        <ToggleButtonGroup
                            value={filter.flagFilter}
                            exclusive
                            onChange={(_, value) => {
                                if (value !== null) {
                                    setFilter({
                                        ...filter,
                                        flagFilter: value as FlagFilter,
                                    })
                                }
                            }}
                            aria-label="Flag filter"
                            style={{ width: '100%' }}
                        >
                            <ToggleButton
                                value="no_filter"
                                aria-label="No Filter"
                                style={{ flex: 1, textTransform: 'none' }}
                            >
                                All
                            </ToggleButton>
                            <ToggleButton
                                value="has_flag"
                                aria-label="Has Flag"
                                style={{ flex: 1, textTransform: 'none' }}
                            >
                                Flagged
                            </ToggleButton>
                            <ToggleButton
                                value="no_flag"
                                aria-label="No Flag"
                                style={{ flex: 1, textTransform: 'none' }}
                            >
                                Not Flagged
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Container>

                    <Divider />

                    {/* Confidence Threshold */}
                    <Container style={{ flexDirection: 'column' }}>
                        <span style={labelStyle}>Confidence Threshold</span>
                        <Container
                            style={{
                                alignItems: 'center',
                                gap: theme.spacing(1),
                            }}
                        >
                            <FormControl style={{ minWidth: 50 }}>
                                <Select
                                    value={filter.confidenceOperator}
                                    onChange={(e) =>
                                        setFilter({
                                            ...filter,
                                            confidenceOperator: e.target
                                                .value as ConfidenceOperator,
                                        })
                                    }
                                >
                                    <MenuItem value="gt">&gt;</MenuItem>
                                    <MenuItem value="lt">&lt;</MenuItem>
                                </Select>
                            </FormControl>
                            <span
                                style={{
                                    ...typographyStyle(theme.typography.body1),
                                    fontWeight: 700,
                                    minWidth: 36,
                                    textAlign: 'center',
                                }}
                            >
                                {localThreshold}%
                            </span>
                        </Container>
                        <Slider
                            value={localThreshold}
                            onChange={(_, value) =>
                                setLocalThreshold(value as number)
                            }
                            onChangeCommitted={(_, value) =>
                                setFilter({
                                    ...filter,
                                    confidenceThreshold: value as number,
                                })
                            }
                            min={0}
                            max={100}
                            step={5}
                            valueLabelDisplay="auto"
                            style={{ marginTop: theme.spacing(1) }}
                        />
                    </Container>

                    <Divider />

                    {/* Service Multi-Select */}
                    <FormControl style={{ width: '100%' }}>
                        <InputLabel id="filter-service-label">
                            Service
                        </InputLabel>
                        <Select
                            labelId="filter-service-label"
                            multiple
                            open={serviceOpen}
                            onOpen={() => {
                                setServiceSearch('')
                                setServiceOpen(true)
                            }}
                            onClose={() => setServiceOpen(false)}
                            value={filter.services}
                            onChange={(e) => {
                                setFilter({
                                    ...filter,
                                    services: e.target.value as number[],
                                })
                                setServiceOpen(false)
                            }}
                            MenuProps={{
                                PaperProps: { style: { maxHeight: 250 } },
                                autoFocus: false,
                            }}
                            renderValue={(selected) =>
                                (selected as number[]).map((id) => {
                                    const svc = allServices.find(
                                        (s) => s.id === id,
                                    )
                                    return (
                                        <Chip
                                            key={id}
                                            label={svc?.name ?? id}
                                            className={classes.chip}
                                            size="small"
                                        />
                                    )
                                })
                            }
                        >
                            <ListSubheader style={{ backgroundColor: '#fff' }}>
                                <TextField
                                    size="small"
                                    autoFocus
                                    placeholder="Search services..."
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    value={serviceSearch}
                                    onChange={(e) =>
                                        setServiceSearch(e.target.value)
                                    }
                                    onKeyDown={(e) => e.stopPropagation()}
                                />
                            </ListSubheader>
                            {filteredServices.map((svc) => (
                                <MenuItem key={svc.id} value={svc.id}>
                                    <Checkbox
                                        checked={filter.services.includes(
                                            svc.id,
                                        )}
                                        color="primary"
                                        size="small"
                                        style={{ padding: 2, marginRight: 4 }}
                                    />
                                    {svc.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Vendor Multi-Select */}
                    <FormControl style={{ width: '100%' }}>
                        <InputLabel id="filter-vendor-label">Vendor</InputLabel>
                        <Select
                            labelId="filter-vendor-label"
                            multiple
                            open={vendorOpen}
                            onOpen={() => {
                                setVendorSearch('')
                                setVendorOpen(true)
                            }}
                            onClose={() => setVendorOpen(false)}
                            value={filter.vendors}
                            onChange={(e) => {
                                setFilter({
                                    ...filter,
                                    vendors: e.target.value as number[],
                                })
                                setVendorOpen(false)
                            }}
                            MenuProps={{
                                PaperProps: { style: { maxHeight: 250 } },
                                autoFocus: false,
                            }}
                            renderValue={(selected) =>
                                (selected as number[]).map((id) => {
                                    const v = allVendors.find(
                                        (vn) => vn.id === id,
                                    )
                                    return (
                                        <Chip
                                            key={id}
                                            label={v?.name ?? id}
                                            className={classes.chip}
                                            size="small"
                                        />
                                    )
                                })
                            }
                        >
                            <ListSubheader style={{ backgroundColor: '#fff' }}>
                                <TextField
                                    size="small"
                                    autoFocus
                                    placeholder="Search vendors..."
                                    fullWidth
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                    value={vendorSearch}
                                    onChange={(e) =>
                                        setVendorSearch(e.target.value)
                                    }
                                    onKeyDown={(e) => e.stopPropagation()}
                                />
                            </ListSubheader>
                            {filteredVendors.map((v) => (
                                <MenuItem key={v.id} value={v.id}>
                                    <Checkbox
                                        checked={filter.vendors.includes(v.id)}
                                        color="primary"
                                        size="small"
                                        style={{ padding: 2, marginRight: 4 }}
                                    />
                                    {v.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <Divider />

                    {/* Unit — Infrastructure Tree */}
                    <Container
                        style={{
                            flex: 1,
                            overflow: 'auto',
                            minHeight: 150,
                            marginBottom: theme.spacing(1),
                        }}
                    >
                        <Finder
                            root={tree}
                            locationSelection={locationSelection}
                            onClickFolder={(location) =>
                                onSelectLocation(location)
                            }
                            onClickUnit={(location) =>
                                onSelectLocation(location)
                            }
                            selectedIcon={<></>}
                            getUnitAdornment={(unit) => (
                                <Container style={{ flex: 1 }}>
                                    <div style={{ flex: 1 }} />
                                    <Checkbox
                                        checked={
                                            locationSelection.unit[unit.id] !==
                                            undefined
                                        }
                                        onClick={() =>
                                            onSelectLocation({
                                                type: 'unit',
                                                location: unit,
                                            })
                                        }
                                    />
                                </Container>
                            )}
                            getFolderAdornment={(folder) => (
                                <Container style={{ flex: 1 }}>
                                    <div style={{ flex: 1 }} />
                                    <Checkbox
                                        color="primary"
                                        checked={
                                            locationSelection.folder[
                                                folder.id
                                            ] !== undefined
                                        }
                                        onClick={(e) => {
                                            e.stopPropagation()
                                            onSelectLocation({
                                                type: 'folder',
                                                location: folder,
                                            })
                                        }}
                                    />
                                </Container>
                            )}
                        />
                    </Container>
                </Container>
            </Container>
        </SideDrawer>
    )
}
